import { isDefined, isNonEmpty } from '@sgme/fp';
import type { QuoteResultDto } from 'apiConnector/quote/dto/quoteResultDto';
import type { DynamicPersistedTabContent } from 'apiConnector/tabs/tabs.model';
import { parseISO } from 'date-fns/parseISO';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import { BookingAck } from 'epic/common-form/Booking';
import type { FormError } from 'models/formError';
import type { DynamicFormResult } from 'models/forms/DynamicFormResult';
import { AllowedRequests } from 'models/forms/FormResult';
import { ReadonlyValueDefinition } from 'models/metadata/readonlyValue.definition';
import type { RegulatoryDocument } from 'models/streaming';
import { buildRemunerationMode, getInstrumentContractName, getInstrumentName } from 'state/common-form/form.helper';
import type { QuoteAction } from 'state/common-form/infos/infos.model';
import { formEnrichedDataReceived } from 'state/formData/formData.action';
import type { DataById } from 'state/formData/formData.model';
import { checkFormEnrichmentRequestNeededThunk } from 'state/formData/formData.thunks';
import { patchedFieldsComputed } from 'state/formData/patchedFields/patchedFields.actions';
import { computePatchedFieldsDiff } from 'state/formData/patchedFields/patchedFields.helper';
import { commonFormActions } from 'state/forms/actions/form';
import { commonQuoteAction, DynamicQuoteReceived } from 'state/forms/actions/quote';
import { commonStreamActions } from 'state/forms/actions/stream';
import { commonTradeAction, TradeAuthReceived } from 'state/forms/actions/trade';
import { triggerDocumentationFetchThunk } from 'state/forms/documentation.thunks';
import {
  dispatchCommonFormActionsForWorkspace,
  tradeAuthorizationReceivedWithBackToBackInitThunk,
} from 'state/forms/thunks';
import type { ProductGraph } from 'state/product-description/model';
import {
  dashboardTileCreating,
  dashboardTileDuplicating,
  dashboardTileRemoved,
} from 'state/quotes/dashboard/dashboard.actions';
import type { QuoteDetailsData } from 'state/quotes/details/details.model';
import type { BookingType, QuoteType } from 'state/quotes/infos/infos.model';
import { pendingQuoteDetailsSaved } from 'state/quotes/pendingDetails/pendingDetails.action';
import { priceImprovementInitialized } from 'state/quotes/priceEnhancements/priceImprovementRequest/priceImprovementRequest.actions';
import { notUsedQuoteCleanedThunk } from 'state/quotes/quotes.thunks';
import {
  costAndChargeReloaded,
  kidDocumentReceived,
  kidDocumentReloaded,
  kidDocumentTimeout,
  productGovernanceAssessmentReloaded,
  productGovernanceDocumentReloaded,
  termsheetDocumentReceived,
  tradeAuthorizationReceived,
  tradeAuthorizationTimeout,
} from 'state/quotes/regulations/regulation.actions';
import { type QuoteWithRegulation } from 'state/quotes/results/result.model';
import type { NextTradeAuthorizationResult } from 'state/quotes/trades/trades.model';
import { isQuoteTypeWithRegulation } from 'state/quotes/utils';
import { getDynamicFormQuoteId, getFormEnrichedData, getFormRequestType } from 'state/selectors';
import type { Thunk } from 'state/state.common';
import { updateSidePanelMetadata } from 'state/uiPanelState/sidePanelUi/sidePanelUi.action';
import type { TabsForWorkspace, WorkspaceType } from 'state/workspaces/model';
import { workspaceTabCloned, workspaceTabFrozen, workspaceTabUnfrozen } from 'state/workspaces/workspace.actions';
import { getFrontLogger } from 'utils/loggers/FrontLogger';

import type { FormBlockMetadataById } from '../../models/metadata/block.definition';
import type { FieldMetadataById } from '../common-form/metaData/fields/fields.model';
import {
  type DynamicBookingTradeDone,
  dynamicAllBookingResultsReceived,
  dynamicBookingAcked,
  dynamicBookingQuoteFailed,
  dynamicBookingRequested,
  dynamicBookingRequestFailed,
  dynamicBookingTradeAuthFailed,
  dynamicBookingTradeRejected,
} from './dynamicForm.action';

const dispatchCommonFormActions = dispatchCommonFormActionsForWorkspace('dynamic');

export function defaultFormReceivedThunk(
  tabId: string,
  blocksMetadata: FormBlockMetadataById,
  fieldsMetadata: FieldMetadataById,
  productDescription: ProductGraph,
  values: DataById,
  version: number,
  negotiationId: string | undefined,
  actions: QuoteAction[],
  quoteQuery: unknown,
  allowedRequests: AllowedRequests,
  validationErrors?: FormError[],
  sendWholeFormData?: boolean,
  etag?: string,
): Thunk<void> {
  return dispatch => {
    dispatch(
      commonFormActions.defaultDynamicFormReceived(
        tabId,
        getInstrumentName(values),
        getInstrumentContractName(values),
        productDescription,
        blocksMetadata,
        fieldsMetadata,
        version,
        negotiationId,
        actions,
        quoteQuery,
        buildRemunerationMode(values),
        validationErrors,
        etag,
        sendWholeFormData,
      ),
    );

    dispatchCommonFormActions(dispatch, tabId, values, allowedRequests);

    dispatch(triggerDocumentationFetchThunk(tabId, 'dynamic'));
  };
}

export function workspaceTabClonedThunk<W extends WorkspaceType>(
  oldTabId: string,
  workspaceType: W,
  tabType: TabsForWorkspace[W],
): Thunk<void> {
  return (dispatch, getState, { getNewGuid, selectors: { getDynamicFormInfos } }) => {
    const state = getState();
    const infos = getDynamicFormInfos(state, oldTabId);
    const enrichedData = getFormEnrichedData(state, oldTabId);

    if (infos !== undefined) {
      const newTabId = getNewGuid();
      dispatch(
        workspaceTabCloned(
          oldTabId,
          newTabId,
          infos.negotiationId,
          workspaceType,
          tabType,
          enrichedData.ProductType as string,
          enrichedData.ProductSubtype as string,
        ),
      );
    }
  };
}

export function dynamicFormEnrichmentReceivedThunk(
  tabId: string,
  blocksMetadata: FormBlockMetadataById,
  fieldsMetadata: FieldMetadataById,
  productGraph: ProductGraph,
  values: DataById,
  version: number,
  actions: QuoteAction[],
  quoteQuery: unknown,
  allowedRequests: AllowedRequests,
  validationErrors?: FormError[],
): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const previousEnriched = getFormEnrichedData(state, tabId);
    const patched = computePatchedFieldsDiff(previousEnriched, values, fieldsMetadata);
    dispatch(patchedFieldsComputed(tabId, patched));

    dispatch(
      commonFormActions.dynamicFormEnrichmentReceived(
        tabId,
        getInstrumentName(values),
        getInstrumentContractName(values),
        blocksMetadata,
        fieldsMetadata,
        productGraph,
        version,
        actions,
        quoteQuery,
        buildRemunerationMode(values),
        validationErrors,
      ),
    );

    dispatchCommonFormActions(dispatch, tabId, values, allowedRequests);

    dispatch(checkFormEnrichmentRequestNeededThunk(tabId, tabId, 'dynamic', fieldsMetadata));

    dispatch(triggerDocumentationFetchThunk(tabId, 'dynamic'));
  };
}

export function dynamicQuoteRequestedThunk(
  tabId: string,
  quoteType: QuoteType,
  refreshedQuoteId?: string,
): Thunk<void> {
  return (dispatch, getState, { selectors: { getDynamicFormQuoteId, getWorkspaceTabType }, getNewGuid, getNow }) => {
    const state = getState();
    const quoteId = getDynamicFormQuoteId(state, tabId);
    const requestType = getFormRequestType(state, 'dynamic', tabId);
    const frontReconciliationId = getNewGuid();

    if (getWorkspaceTabType(state, tabId) === undefined) {
      getFrontLogger().warn(`dynamicQuoteRequestedThunk: tabId ${tabId} no longer exist`);
      return;
    }

    if (quoteId) {
      dispatch(dynamicQuoteRemovedThunk(tabId, quoteId));
    }
    if (refreshedQuoteId !== undefined) {
      dispatch(dashboardTileDuplicating(frontReconciliationId, refreshedQuoteId, 'dynamic'));
    } else {
      dispatch(dashboardTileCreating(frontReconciliationId, 'dynamic'));
    }
    const isReprice = refreshedQuoteId !== undefined;

    if (requestType === 'RequestForStream') {
      dispatch(commonStreamActions.streamRequested('dynamic', tabId, quoteType, getNow().getTime(), isReprice));
    } else {
      dispatch(
        commonQuoteAction.quoteRequested(
          'dynamic',
          tabId,
          quoteType,
          getNow().getTime(),
          frontReconciliationId,
          isReprice,
        ),
      );
    }

    dispatch(workspaceTabFrozen(tabId, 'dynamic', true));
  };
}

export function dynamicQuoteAckedThunk(
  tabId: string,
  quoteId: string,
  quoteType: QuoteType,
  externalQuoteId: string,
  negotiationId: string | undefined,
  estimatedQuotingDurationInMillis: number,
  quoteRequestStartingTimeInMillis: number,
  newVersion: number,
  isExecOnlyAddon: boolean,
  quoteDetails: QuoteDetailsData,
  frontReconciliationId: string,
  sidePanelDetails: readonly ReadonlyValueDefinition[] | undefined,
): Thunk<void> {
  return (dispatch, _, { getNow }) => {
    const isKidExpected = isQuoteTypeWithRegulation(quoteType);

    dispatch(
      commonQuoteAction.quoteAcked(
        'dynamic',
        tabId,
        quoteId,
        quoteType,
        externalQuoteId,
        negotiationId,
        estimatedQuotingDurationInMillis,
        quoteRequestStartingTimeInMillis,
        newVersion,
        isExecOnlyAddon,
        isKidExpected,
        frontReconciliationId,
        quoteDetails,
        getNow(),
        sidePanelDetails,
      ),
    );

    dispatch(
      pendingQuoteDetailsSaved(quoteId, 'dynamic', {
        start: new Date(quoteRequestStartingTimeInMillis),
        durationInMs: estimatedQuotingDurationInMillis,
      }),
    );
  };
}

export function dynamicStreamingQuoteAckedThunk(
  tabId: string,
  quoteId: string,
  quoteType: QuoteType,
  externalQuoteId: string,
  negotiationId: string | undefined,
  estimatedQuotingDurationInMillis: number,
  quoteRequestStartingTimeInMillis: number,
  newVersion: number,
  isExecOnlyAddon: boolean,
): Thunk<void> {
  return (dispatch, _, { getNow }) => {
    dispatch(
      commonStreamActions.streamingQuoteAcked(
        'dynamic',
        tabId,
        quoteId,
        quoteType,
        externalQuoteId,
        negotiationId,
        estimatedQuotingDurationInMillis,
        quoteRequestStartingTimeInMillis,
        newVersion,
        isExecOnlyAddon,
        getNow(),
      ),
    );

    dispatch(
      pendingQuoteDetailsSaved(quoteId, 'dynamic', {
        start: new Date(quoteRequestStartingTimeInMillis),
        durationInMs: estimatedQuotingDurationInMillis,
      }),
    );
  };
}

export function quoteResultReceivedThunk(
  tabId: string,
  quoteId: string,
  data: QuoteResultDto<DynamicFormResult>,
): Thunk<void> {
  return (dispatch, getState, { selectors: { getDynamicFormInfos }, getNow }) => {
    const state = getState();

    const infos = getDynamicFormInfos(state, tabId);
    const now = getNow();

    const formResult = data.formResult;
    const isQuoteRejected = data.status === 'Rejected' || data.status === 'Failed';

    dispatch(commonQuoteAction.quoteReceived('dynamic', tabId, quoteId, data, now));
    dispatch(updateSidePanelMetadata(tabId, { tradeWay: data.tradeWay }));

    if (isQuoteRejected) {
      dispatch(workspaceTabUnfrozen(tabId, 'dynamic', undefined));
    }

    dispatch(priceImprovementInitialized(quoteId));
    if (formResult !== undefined && infos !== undefined) {
      const { values, blocksMetadata, fieldsMetadata, productDescription, quoteQuery, actions, errors, version } =
        formResult;
      const previousEnriched = getFormEnrichedData(state, tabId);
      const patched = computePatchedFieldsDiff(previousEnriched, values, fieldsMetadata);
      dispatch(patchedFieldsComputed(tabId, patched));
      dispatch(
        commonFormActions.dynamicFormEnrichmentReceived(
          tabId,
          infos.instrumentName,
          infos.instrumentContractName,
          blocksMetadata,
          fieldsMetadata,
          productDescription,
          version,
          actions,
          quoteQuery,
          infos.remunerationMode,
          errors,
        ),
      );
      dispatch(formEnrichedDataReceived(tabId, tabId, values, 'dynamic'));
      dispatch(triggerDocumentationFetchThunk(tabId, 'dynamic'));
    }
  };
}

export function dynamicQuoteRemovedThunk(tabId: string, quoteId?: string): Thunk<void> {
  return (dispatch, getState) => {
    if (!isDefined(quoteId)) {
      const state = getState();
      quoteId = getDynamicFormQuoteId(state, tabId);
    }

    if (isDefined(quoteId)) {
      dispatch(commonQuoteAction.quoteRemoved('dynamic', tabId, quoteId));
      dispatch(notUsedQuoteCleanedThunk(tabId, quoteId));
    } else {
      dispatch(commonQuoteAction.quoteRemoved('dynamic', tabId));
    }
  };
}

export function dynamicQuoteRequestFailedThunk(
  tabId: string,
  error: ApiErrorMessage,
  frontReconciliationId: string,
): Thunk<void> {
  return dispatch => {
    dispatch(commonQuoteAction.quoteRequestFailed('dynamic', tabId, error, frontReconciliationId));
    if (typeof error !== 'string') {
      if (error.newVersion !== undefined) {
        dispatch(commonFormActions.updateRfqVersion('dynamic', tabId, error.newVersion));
      }

      if (error.status === 400 && error.functionalErrorType === 'rfq-version-mismatch') {
        dispatch(commonFormActions.fetchFormEnrichmentFailed('dynamic', tabId, error as Error));
      }
    }
  };
}

export function dynamicTabReloadedThunk(tabId: string, tabContent: DynamicPersistedTabContent): Thunk<void> {
  return dispatch => {
    const {
      negotiationId,
      blocksMetadata,
      fieldsMetadata,
      productDescription,
      values,
      version,
      actions,
      quoteQuery,
      errors: validationErrors,
      etag,
      quoteId,
      sendWholeFormData,
      allowedRequests,
    } = tabContent;

    dispatch(
      dynamicFormReloadedThunk(
        tabId,
        blocksMetadata,
        fieldsMetadata,
        productDescription,
        values,
        version,
        negotiationId,
        actions,
        quoteQuery,
        allowedRequests,
        validationErrors,
        etag,
        sendWholeFormData,
      ),
    );
    if (quoteId !== undefined) {
      dispatch(commonQuoteAction.previousQuoteLoadRequested('dynamic', tabId, quoteId));
    }
  };
}

export function dynamicFormReloadedThunk(
  tabId: string,
  blocksMetadata: FormBlockMetadataById,
  fieldsMetadata: FieldMetadataById,
  productDescription: ProductGraph,
  values: DataById,
  version: number,
  negotiationId: string | undefined,
  actions: QuoteAction[],
  quoteQuery: unknown,
  allowedRequests: AllowedRequests,
  validationErrors?: FormError[],
  etag?: string,
  sendWholeFormData?: boolean,
): Thunk<void> {
  return (dispatch, getState, { selectors: { getWorkspaceTabType } }) => {
    const state = getState();

    if (getWorkspaceTabType(state, tabId) === undefined) {
      getFrontLogger().warn(`dynamicFormReloadedThunk: tabId ${tabId} no longer exist`);
      return;
    }

    dispatch(
      commonFormActions.dynamicFormReloaded(tabId, {
        instrumentName: getInstrumentName(values),
        instrumentContractName: getInstrumentContractName(values),
        blocksMetadata,
        fieldsMetadata,
        productGraph: productDescription,
        version,
        actions,
        quoteQuery,
        remunerationMode: buildRemunerationMode(values),
        validationErrors: isNonEmpty(validationErrors) ? validationErrors : undefined,
        negotiationId,
        etag,
        sendWholeFormData,
      }),
    );

    dispatchCommonFormActions(dispatch, tabId, values, allowedRequests);

    dispatch(triggerDocumentationFetchThunk(tabId, 'dynamic'));
  };
}

export function dynamicPreviousQuoteLoadedThunk(
  tabId: string,
  quoteId: string,
  quoteWithRegulation: QuoteWithRegulation,
): Thunk<void> {
  return dispatch => {
    dispatch(commonQuoteAction.previousQuoteLoaded('dynamic', tabId, quoteId, quoteWithRegulation.quote));

    if (quoteWithRegulation.costAndCharge !== undefined) {
      dispatch(costAndChargeReloaded('dynamic', tabId, quoteId, quoteWithRegulation.costAndCharge));
    }

    if (quoteWithRegulation.kidDocumentation !== undefined) {
      dispatch(kidDocumentReloaded('dynamic', tabId, quoteId, quoteWithRegulation.kidDocumentation));
    }

    if (quoteWithRegulation.productGovernanceDocumentation !== undefined) {
      dispatch(
        productGovernanceDocumentReloaded(
          'dynamic',
          tabId,
          quoteId,
          quoteWithRegulation.productGovernanceDocumentation,
        ),
      );
    }

    if (quoteWithRegulation.productGovernanceAssessment !== undefined) {
      dispatch(
        productGovernanceAssessmentReloaded('dynamic', tabId, quoteId, quoteWithRegulation.productGovernanceAssessment),
      );
    }
  };
}

export function dynamicBookingAckedThunk(
  ack: BookingAck,
  {
    tabId,
    withExecution,
    frontReconciliationId,
    quoteId,
  }: {
    tabId: string;
    withExecution: boolean;
    frontReconciliationId: string;
    quoteId: string;
  },
): Thunk<void> {
  return dispatch => {
    dispatch(
      dynamicBookingAcked(tabId, ack.negotiationId, quoteId, ack.rfqExternalId, withExecution, frontReconciliationId),
    );
    dispatch(commonFormActions.updateRfqVersion('dynamic', tabId, ack.newVersion));
  };
}

export function dynamicBookingRequestedThunk(tabId: string, bookingType: BookingType): Thunk<void> {
  return (dispatch, getState, { getNewGuid, selectors: { getWorkspaceTabType } }) => {
    const state = getState();
    const frontReconciliationId = getNewGuid();
    const quoteId = getNewGuid();

    if (getWorkspaceTabType(state, tabId) === undefined) {
      getFrontLogger().warn(`dynamicBookingRequestedThunk: tabId ${tabId} no longer exist`);
      return;
    }

    dispatch(dynamicQuoteRemovedThunk(tabId, quoteId));
    dispatch(dashboardTileCreating(frontReconciliationId, 'dynamic'));

    const withExecution = bookingType === 'BookWithExecution';
    dispatch(dynamicBookingRequested(tabId, withExecution, frontReconciliationId, quoteId));
    dispatch(workspaceTabFrozen(tabId, 'dynamic', true));
  };
}

export function dynamicBookingRequestFailedThunk(
  tabId: string,
  error: ApiErrorMessage,
  frontReconciliationId: string,
): Thunk<void> {
  return dispatch => {
    dispatch(dynamicBookingRequestFailed(tabId, error, frontReconciliationId));
    if (typeof error !== 'string') {
      if (error.newVersion !== undefined) {
        dispatch(commonFormActions.updateRfqVersion('dynamic', tabId, error.newVersion));
      }

      if (error.status === 400 && error.functionalErrorType === 'rfq-version-mismatch') {
        dispatch(commonFormActions.fetchFormEnrichmentFailed('dynamic', tabId, error as Error));
      }
    }
  };
}

export function dynamicFormTabRollbackedThunk(tabId: string, negotiationId: string): Thunk<void> {
  return (dispatch, getState, { selectors: { getDynamicFormQuoteId } }) => {
    const state = getState();
    const quoteId = getDynamicFormQuoteId(state, tabId);
    if (quoteId) {
      dispatch(dynamicQuoteRemovedThunk(tabId, quoteId));
    }
    dispatch(commonFormActions.formTabRollbacked('dynamic', tabId, negotiationId));
  };
}

export function dynamicQuoteTradeCancelledThunk(tabId: string): Thunk<void> {
  return (dispatch, getState, { selectors: { getDynamicFormInfos } }) => {
    const state = getState();
    const formInfos = getDynamicFormInfos(state, tabId);
    const quoteId = formInfos?.quoteId;
    const requestType = getFormRequestType(state, 'dynamic', tabId);

    if (requestType === 'RequestForStream') {
      dispatch(
        commonStreamActions.streamingCancelled(
          'dynamic',
          tabId,
          'Tradable',
          formInfos?.negotiationId,
          quoteId,
          formInfos?.instrumentContractName,
          formInfos?.instrumentName,
        ),
      );
      dispatch(workspaceTabUnfrozen(tabId, 'dynamic', undefined));
    } else {
      dispatch(
        commonQuoteAction.quoteCancelled(
          'dynamic',
          tabId,
          formInfos?.negotiationId,
          quoteId,
          formInfos?.instrumentContractName,
          formInfos?.instrumentName,
        ),
      );
    }
    if (quoteId) {
      dispatch(commonQuoteAction.quoteStale('dynamic', tabId, quoteId, false));
    }
  };
}

export function dynamicQuoteTradeRequestedThunk(tabId: string, quoteId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const requestType = getFormRequestType(state, 'dynamic', tabId);
    if (!quoteId) {
      return;
    }
    if (requestType === 'RequestForStream') {
      dispatch(commonStreamActions.streamingTradeRequested('dynamic', tabId, quoteId));
    } else {
      dispatch(commonTradeAction.quoteTradeRequested('dynamic', tabId, quoteId));
    }
  };
}

export function dynamicQuoteCancelledThunk(tabId: string): Thunk<void> {
  return (dispatch, getState, { selectors: { getDynamicFormInfos } }) => {
    const state = getState();
    const formInfos = getDynamicFormInfos(state, tabId);
    const quoteId = formInfos?.quoteId;
    const requestType = getFormRequestType(state, 'dynamic', tabId);
    if (requestType === 'RequestForStream') {
      dispatch(
        commonStreamActions.streamingCancelled(
          'dynamic',
          tabId,
          'Indicative',
          formInfos?.negotiationId,
          quoteId,
          formInfos?.instrumentContractName,
          formInfos?.instrumentName,
        ),
      );
    } else {
      dispatch(
        commonQuoteAction.quoteCancelled(
          'dynamic',
          tabId,
          formInfos?.negotiationId,
          quoteId,
          formInfos?.instrumentContractName,
          formInfos?.instrumentName,
        ),
      );
    }
    if (quoteId) {
      dispatch(dashboardTileRemoved(quoteId));
      dispatch(dynamicQuoteRemovedThunk(tabId, quoteId));
    }
  };
}

export function dynamicKidDocumentReceivedThunk(
  tabId: string,
  quoteId: string,
  doc: RegulatoryDocument,
  replacePreviousResources: boolean = false,
): Thunk<void> {
  return dispatch => {
    dispatch(kidDocumentReceived('dynamic', tabId, quoteId, doc, replacePreviousResources));
  };
}

export function dynamicTermsheetDocumentReceivedThunk(
  tabId: string,
  quoteId: string,
  doc: RegulatoryDocument,
  replacePreviousResources: boolean = false,
): Thunk<void> {
  return dispatch => {
    dispatch(termsheetDocumentReceived('dynamic', tabId, quoteId, doc, replacePreviousResources));
  };
}

export function dynamicKidDocumentTimeoutThunk(tabId: string, quoteId: string): Thunk<void> {
  return dispatch => {
    dispatch(kidDocumentTimeout('dynamic', tabId, quoteId));
  };
}
export function dynamicTradeAuthReceivedThunk(
  tabId: string,
  quoteId: string,
  tradeAuth: NextTradeAuthorizationResult,
): Thunk<void> {
  return (dispatch, _, { getNow }) => {
    const tradeAuthCreatedOn = tradeAuth.createdOn;

    const when =
      tradeAuth.status === 'Granted' && isNonEmpty(tradeAuthCreatedOn) ? parseISO(tradeAuthCreatedOn) : getNow();

    dispatch(commonTradeAction.tradeAuthReceived('dynamic', tabId, quoteId, tradeAuth, when));
    dispatch(tradeAuthorizationReceivedWithBackToBackInitThunk('dynamic', tabId, quoteId));
  };
}
export function dynamicStreamTradeAuthReceivedThunk(tabId: string, quoteId: string): Thunk<void> {
  return (dispatch, _, { getNow }) => {
    const now = getNow();
    dispatch(commonStreamActions.streamingTradeAuthReceived('dynamic', tabId, quoteId, now));
    dispatch(tradeAuthorizationReceivedWithBackToBackInitThunk('dynamic', tabId, quoteId));
  };
}

export function dynamicTradeAuthTimeoutThunk(tabId: string, quoteId: string): Thunk<void> {
  return dispatch => {
    dispatch(commonTradeAction.tradeAuthTimeout('dynamic', tabId, quoteId));
    dispatch(tradeAuthorizationTimeout(tabId, quoteId));
  };
}

export function dynamicBookingTradeAuthFailureReceivedThunk(
  tabId: string,
  quoteId: string,
  tradeAuth: NextTradeAuthorizationResult,
): Thunk<void> {
  return (dispatch, _, { getNow }) => {
    const now = getNow();
    dispatch(commonTradeAction.tradeAuthReceived('dynamic', tabId, quoteId, tradeAuth, now));
    dispatch(tradeAuthorizationReceived('dynamic', tabId, quoteId));
    dispatch(dynamicBookingTradeAuthFailed(tabId, quoteId));
  };
}

export function dynamicBookingTradeAuthTimeoutThunk(tabId: string, quoteId: string): Thunk<void> {
  return dispatch => {
    dispatch(commonTradeAction.tradeAuthTimeout('dynamic', tabId, quoteId));
    dispatch(tradeAuthorizationTimeout(tabId, quoteId));
    dispatch(commonQuoteAction.quoteStale('dynamic', tabId, quoteId, false));
  };
}

export function quoteResultReceivedForBookingThunk(
  tabId: string,
  quoteId: string,
  data: QuoteResultDto<DynamicFormResult>,
): Thunk<void> {
  return (dispatch, getState, { selectors: { getDynamicFormInfos }, getNow }) => {
    const state = getState();
    const infos = getDynamicFormInfos(state, tabId);
    const now = getNow();

    const formResult = data.formResult;

    dispatch(commonQuoteAction.quoteReceived('dynamic', tabId, quoteId, data, now));
    if (formResult !== undefined && infos !== undefined) {
      dispatch(
        commonFormActions.dynamicFormEnrichmentReceived(
          tabId,
          infos.instrumentName,
          infos.instrumentContractName,
          formResult.blocksMetadata,
          formResult.fieldsMetadata,
          formResult.productDescription,
          formResult.version,
          formResult.actions,
          formResult.quoteQuery,
          infos.remunerationMode,
          formResult.errors,
        ),
      );
    }
  };
}

export function quoteResultFailedForBookingThunk(
  tabId: string,
  quoteId: string,
  data: QuoteResultDto<DynamicFormResult>,
): Thunk<void> {
  return (dispatch, getState, { selectors: { getDynamicFormInfos }, getNow }) => {
    const state = getState();
    const infos = getDynamicFormInfos(state, tabId);
    const now = getNow();

    const formResult = data.formResult;

    dispatch(commonQuoteAction.quoteReceived('dynamic', tabId, quoteId, data, now));
    if (formResult !== undefined && infos !== undefined) {
      dispatch(
        commonFormActions.dynamicFormEnrichmentReceived(
          tabId,
          infos.instrumentName,
          infos.instrumentContractName,
          formResult.blocksMetadata,
          formResult.fieldsMetadata,
          formResult.productDescription,
          formResult.version,
          formResult.actions,
          formResult.quoteQuery,
          infos.remunerationMode,
          formResult.errors,
        ),
      );
    }
    dispatch(dynamicBookingTradeRejected(tabId, quoteId, data.rejections!, now));
    dispatch(workspaceTabUnfrozen(tabId, 'dynamic', false));
  };
}

export function dynamicAllBookingResultsReceivedThunk(
  quoteId: string,
  tabId: string,
  quoteReceived?: DynamicQuoteReceived,
  tradeAuthReceived?: TradeAuthReceived,
  tradeReceived?: DynamicBookingTradeDone,
): Thunk<void> {
  return dispatch => {
    if (quoteReceived === undefined || tradeAuthReceived === undefined || tradeReceived === undefined) {
      dispatch(
        commonTradeAction.quoteTradeFailed('dynamic', tabId, quoteId, { message: 'Unknown error' }, 'Unknown error'),
      );
      return;
    }
    const { status: quoteStatus, formResult } = quoteReceived.quote;

    if (quoteStatus !== 'Quoted') {
      dispatch(dynamicBookingQuoteFailed(tabId, quoteId));
      return;
    }

    const quoteVersion = formResult?.version;
    if (isDefined(quoteVersion)) {
      dispatch(commonFormActions.updateRfqVersion('dynamic', tabId, quoteVersion));
    }

    const tradeAuthStatus = tradeAuthReceived.status;
    const tradeStatus = tradeReceived.status;

    if (quoteStatus === 'Quoted' && tradeAuthStatus === 'Granted' && tradeStatus === 'FULLY_EXECUTED') {
      dispatch(dynamicAllBookingResultsReceived(quoteId, tabId));
    }
  };
}
